import type {
  AnnotatedJsonSchema,
  EntityDetailSchema,
} from '../../types/schemas';
import type { ConfigEntityDetail } from '../../types/entities';

/**
 * Recursively walks entity data alongside its JSON schema and replaces `null`
 * values with sensible defaults so they survive the `removeNullAndEmptyObjects`
 * transformer in the publish pipeline.
 *
 * Rules:
 * - If a field is `null` and the schema has an explicit `default`, use that.
 * - If a field is `null`, `type: "boolean"`, and no explicit default, use `false`.
 * - Missing required booleans without defaults get `false`.
 * - Missing fields with explicit defaults get the default value.
 */
export const applySchemaDefaults = (
  data: unknown,
  schema: AnnotatedJsonSchema | null | undefined,
): unknown => {
  if (!schema || data === undefined) return data;

  if (data === null) {
    if (schema.default !== undefined) return schema.default;
    if (schema.type === 'boolean') return false;
    return data;
  }

  if (
    schema.type === 'object' &&
    schema.properties &&
    typeof data === 'object' &&
    !Array.isArray(data)
  ) {
    const result = { ...(data as Record<string, unknown>) };
    const requiredFields = Array.isArray(schema.required)
      ? schema.required
      : [];
    Object.entries(schema.properties).forEach(([key, propSchema]) => {
      if (key in result) {
        result[key] = applySchemaDefaults(result[key], propSchema);
      } else if (propSchema.default !== undefined) {
        result[key] = propSchema.default;
      } else if (
        propSchema.type === 'boolean' &&
        requiredFields.includes(key)
      ) {
        result[key] = false;
      }
    });
    return result;
  }

  if (
    schema.type === 'array' &&
    Array.isArray(data) &&
    schema.items &&
    !Array.isArray(schema.items)
  ) {
    return data.map((item: unknown) =>
      applySchemaDefaults(item, schema.items as AnnotatedJsonSchema),
    );
  }

  return data;
};

/**
 * Applies schema defaults to an entity's attributes before publishing.
 *
 * The API response nests domain attributes under
 * `attributes.domainEntityAttributes`, while the schema defines them
 * directly under `properties.attributes.properties`. This function
 * bridges that gap by applying the schema's `attributes` definitions
 * to `domainEntityAttributes` (or directly to `attributes` if
 * `domainEntityAttributes` is not present).
 */
export const applySchemaDefaultsToEntity = (
  entityData: ConfigEntityDetail,
  schema: EntityDetailSchema | null,
): ConfigEntityDetail => {
  if (!schema || !entityData) return entityData;

  const schemaProps = schema.properties;
  const dataSchema = schemaProps?.data;
  const attributesSchema: AnnotatedJsonSchema | undefined =
    schemaProps?.attributes ??
    (dataSchema && 'properties' in dataSchema
      ? dataSchema.properties?.attributes
      : undefined);

  if (!attributesSchema || !('properties' in attributesSchema))
    return entityData;

  const attrs = entityData.attributes;
  if (!attrs) return entityData;

  if (
    'domainEntityAttributes' in attrs &&
    attrs.domainEntityAttributes &&
    typeof attrs.domainEntityAttributes === 'object'
  ) {
    return {
      ...entityData,
      attributes: {
        ...attrs,
        domainEntityAttributes: applySchemaDefaults(
          attrs.domainEntityAttributes,
          attributesSchema,
        ),
      },
    } as ConfigEntityDetail;
  }

  return {
    ...entityData,
    attributes: applySchemaDefaults(attrs, attributesSchema),
  } as ConfigEntityDetail;
};
